'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { Building2, Lock, Mail, Eye, EyeOff, ShieldCheck, ArrowRight, Phone, MessageSquare } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

const MOCK_CREDENTIALS = [
  { email: 'hr@techcorp.com', password: 'corporate123', company: 'TechCorp India Pvt. Ltd.', role: 'HR Manager' },
  { email: 'admin@infosys.com', password: 'admin123', company: 'Infosys Limited', role: 'Admin' },
];

type AuthMethod = 'email' | 'phone';
type OtpStep = 'phone' | 'verify';

export default function CorporateLoginPage() {
  const router = useRouter();

  // Auth method toggle
  const [authMethod, setAuthMethod] = useState<AuthMethod>('email');

  // Email/password state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Phone OTP state
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [otpStep, setOtpStep] = useState<OtpStep>('phone');
  const [successMsg, setSuccessMsg] = useState('');

  // Shared state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const supabase = createClient();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    await new Promise((r) => setTimeout(r, 900));

    const match = MOCK_CREDENTIALS.find(
      (c) => c.email === email.trim().toLowerCase() && c.password === password
    );

    if (match) {
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('corp_user', JSON.stringify({ email: match.email, company: match.company, role: match.role }));
      }
      router.push('/corporate-dashboard');
    } else {
      setError('Invalid email or password. Try hr@techcorp.com / corporate123');
    }
    setLoading(false);
  };

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');

    const cleaned = phone.trim().replace(/\s+/g, '');
    if (!cleaned.startsWith('+')) {
      setError('Please enter phone number with country code (e.g. +91XXXXXXXXXX).');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOtp({ phone: cleaned });
      if (error) throw error;
      setSuccessMsg(`OTP sent to ${cleaned}. Please check your messages.`);
      setOtpStep('verify');
    } catch (err: any) {
      setError(err?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const cleaned = phone.trim().replace(/\s+/g, '');
    try {
      const { error } = await supabase.auth.verifyOtp({
        phone: cleaned,
        token: otp.trim(),
        type: 'sms',
      });
      if (error) throw error;
      router.push('/corporate-dashboard');
    } catch (err: any) {
      setError(err?.message || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetOtpFlow = () => {
    setOtpStep('phone');
    setOtp('');
    setError('');
    setSuccessMsg('');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel — brand */}
      <div className="hidden lg:flex lg:w-1/2 hero-gradient flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 rounded-full bg-sky-300 blur-3xl" />
        </div>
        <div className="relative z-10">
          <Link href="/" className="flex items-center gap-3">
            <AppLogo size={56} />
            <div>
              <div className="text-white font-extrabold text-2xl leading-tight">51 Plus Lab</div>
              <div className="text-sky-200 text-xs font-semibold uppercase tracking-wide">NABL Accredited Diagnostics</div>
            </div>
          </Link>
        </div>
        <div className="relative z-10 space-y-8">
          <div>
            <h1 className="text-white text-4xl font-bold leading-tight mb-4">
              Corporate Health<br />Management Portal
            </h1>
            <p className="text-sky-100 text-lg leading-relaxed">
              Manage bulk employee health screenings, track department-wise reports, and handle invoices — all in one place.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Bulk Bookings', desc: 'Schedule for entire teams' },
              { label: 'Health Tracking', desc: 'Monitor screening status' },
              { label: 'Dept. Reports', desc: 'Analytics by department' },
              { label: 'Invoice Hub', desc: 'Billing & payment records' },
            ].map((f) => (
              <div key={f.label} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                <div className="text-white font-semibold text-sm">{f.label}</div>
                <div className="text-sky-200 text-xs mt-0.5">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-sky-200 text-sm">
          <ShieldCheck size={16} />
          <span>Secured with enterprise-grade encryption</span>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <AppLogo size={48} />
            <div>
              <div className="font-extrabold text-xl text-foreground">51 Plus Lab</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Corporate Portal</div>
            </div>
          </div>

          <div className="mb-6">
            <div className="inline-flex items-center gap-2 bg-secondary text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
              <Building2 size={13} />
              Corporate Login
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">
              {authMethod === 'phone' ? 'Mobile OTP Login' : 'Welcome back'}
            </h2>
            <p className="text-muted-foreground">
              {authMethod === 'phone' ? 'Sign in securely using your mobile number' : 'Sign in to your corporate health dashboard'}
            </p>
          </div>

          {/* Auth method toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-6">
            <button
              type="button"
              onClick={() => { setAuthMethod('email'); setError(''); setSuccessMsg(''); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                authMethod === 'email' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Mail size={14} /> Email
            </button>
            <button
              type="button"
              onClick={() => { setAuthMethod('phone'); setError(''); setSuccessMsg(''); resetOtpFlow(); }}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${
                authMethod === 'phone' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Phone size={14} /> Mobile OTP
            </button>
          </div>

          {/* EMAIL / PASSWORD SECTION */}
          {authMethod === 'email' && (
            <>
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Corporate Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="hr@yourcompany.com"
                      required
                      className="input-field pl-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="input-field pl-10 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-danger/10 border border-danger/20 text-danger text-sm rounded-lg px-4 py-3">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full justify-center text-base py-3"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                      </svg>
                      Signing in…
                    </span>
                  ) : (
                    <>Sign In <ArrowRight size={16} /></>
                  )}
                </button>
              </form>

              <div className="mt-6 p-4 bg-muted rounded-xl border border-border">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Demo credentials:</p>
                <p className="text-xs text-foreground font-mono">hr@techcorp.com / corporate123</p>
              </div>
            </>
          )}

          {/* PHONE OTP SECTION */}
          {authMethod === 'phone' && (
            <>
              {otpStep === 'phone' && (
                <form onSubmit={handleSendOtp} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Mobile Number</label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+91 98765 43210"
                        required
                        className="input-field pl-10"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">Include country code (e.g. +91 for India)</p>
                  </div>

                  {error && (
                    <div className="bg-danger/10 border border-danger/20 text-danger text-sm rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center text-base py-3"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Sending OTP…
                      </span>
                    ) : (
                      <>
                        <MessageSquare size={16} />
                        Send OTP
                      </>
                    )}
                  </button>
                </form>
              )}

              {otpStep === 'verify' && (
                <form onSubmit={handleVerifyOtp} className="space-y-5">
                  {successMsg && (
                    <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3">
                      {successMsg}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Enter OTP</label>
                    <div className="relative">
                      <MessageSquare size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="6-digit OTP"
                        required
                        maxLength={6}
                        className="input-field pl-10 tracking-widest text-center text-lg font-semibold"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1.5">OTP sent to {phone}</p>
                  </div>

                  {error && (
                    <div className="bg-danger/10 border border-danger/20 text-danger text-sm rounded-lg px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full justify-center text-base py-3"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        Verifying…
                      </span>
                    ) : (
                      <>
                        Verify & Sign In
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetOtpFlow}
                    className="w-full text-sm text-muted-foreground hover:text-primary transition-colors text-center py-1"
                  >
                    ← Change number / Resend OTP
                  </button>
                </form>
              )}

              <div className="mt-6 p-4 bg-muted rounded-xl border border-border">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Note:</p>
                <p className="text-xs text-muted-foreground">SMS delivery requires Twilio to be configured in Supabase → Authentication → Phone settings.</p>
              </div>
            </>
          )}

          <div className="mt-6 text-center">
            <Link href="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              ← Back to main site
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
